import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { DocumentData } from "@/types/document.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type NoteMutationProps = {
    tag: string;
    note: string;
    caseTaskUUID: string | null;
    enabled?: boolean;
    title?: string;
    priority?: "low" | "medium" | "high";
    notifyUsers?: string[];
    sendEmail?: boolean;
    documents?: DocumentData[];
};

export const useAddNoteToCase = (
    caseUUID: string,
): UseMutationResult<void, Error, NoteMutationProps> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attributes: NoteMutationProps) => {
            const response = await fetch(apiUrl("/note/case/create"), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    ...attributes,
                    caseUUID,
                }),
            });
            await handleJsonApiError(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["case-notes", caseUUID],
            });
            await queryClient.invalidateQueries({
                queryKey: ["case-tasks", caseUUID],
            });
            await queryClient.invalidateQueries({
                queryKey: ["case-media", caseUUID],
            });
            await queryClient.invalidateQueries({
                queryKey: ["case-notifications"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["notification-history"],
            });
        },
    });
};
